import { useTranslation } from 'react-i18next';
import notificationsBg from '@assets/images/notifications-bg.webp';
import { Flex, Heading } from '@radix-ui/themes';
import { Stepper } from '@/components/common';
import { WizardHeader, WizardScreen } from '@/components/layout';
import { ContinueButton } from '@/components/onboarding';
import { notificationService } from '@/services';
import { useOnboardingStore } from '@/stores/onboardingStore';
import type { OnboardingStepProps } from './types';
import { useSaveAndNext } from './useSaveAndNext';

export function NotificationsStep({ flow }: OnboardingStepProps) {
  const { t } = useTranslation('onboarding');
  const loading = useOnboardingStore(state => state.loading);
  const uid = useOnboardingStore(state => state.uid);
  const setNotificationsEnabled = useOnboardingStore(state => state.setNotificationsEnabled);
  const saveAndNext = useSaveAndNext(flow);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      setNotificationsEnabled(false);
      void saveAndNext();
      return;
    }

    const permission = await Notification.requestPermission();
    const enabled = permission === 'granted';
    setNotificationsEnabled(enabled);

    if (enabled && uid) {
      try {
        await notificationService.registerMessaging(uid);
      } catch (err) {
        console.error('Failed to register FCM during onboarding:', err);
      }
    }

    void saveAndNext();
  };

  return (
    <WizardScreen
      bgPicture={notificationsBg}
      header={
        <WizardHeader
          onBack={flow.back}
          centerContent={
            <Stepper progress={flow.progress} step={flow.step} totalSteps={flow.totalSteps} />
          }
        />
      }
    >
      <Flex direction="column" minHeight="0" height="100%" position="relative">
        <Flex
          direction="column"
          justify="center"
          flexGrow="1"
          minHeight="0"
          gap="4"
          style={{ textAlign: 'center' }}
        >
          <Heading size="5" weight="light">
            {t('steps.notifications.title')}
          </Heading>
          <ContinueButton onClick={() => void requestNotificationPermission()} disabled={loading}>
            {t('actions.setNotificationPreferences')}
          </ContinueButton>
        </Flex>
      </Flex>
    </WizardScreen>
  );
}
